import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import ReportWidget from './components/ReportWidget';
import api from '../../helpers/api';
import { formatDateTimeForAPI } from '../../constants/utils';

const COLOR_PRIMARY = '#3980c0';
const COLOR_UP = '#16a34a';
const COLOR_DOWN = '#dc2626';
const COLOR_NEUTRAL = '#6b7280';

const formatHours = (value) => {
  if (value == null) return '—';
  if (value === 0) return '0h';
  if (value < 1) return `${Math.round(value * 60)}m`;
  if (value < 10) return `${value.toFixed(1)}h`;
  return `${Math.round(value)}h`;
};

const DeltaIndicator = ({ delta }) => {
  if (delta == null) {
    return <span style={{ fontSize: '0.75rem', color: COLOR_NEUTRAL }}><Minus size={12} /> —</span>;
  }
  const positive = delta > 0;
  const color = positive ? COLOR_UP : delta < 0 ? COLOR_DOWN : COLOR_NEUTRAL;
  const Icon = positive ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const sign = positive ? '+' : '';
  return (
    <span style={{ fontSize: '0.75rem', color, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <Icon size={12} />
      {sign}{delta.toFixed(1)}%
    </span>
  );
};

const CounterTile = ({ value, label, color = COLOR_NEUTRAL }) => (
  <div
    className="d-flex flex-column align-items-start"
    style={{
      minWidth: 150,
      padding: '12px 14px',
      border: '1px solid #eef1f5',
      borderRadius: 12,
      background: '#fff',
      flex: '1 1 0',
    }}
  >
    <span className="text-muted" style={{ fontSize: '0.78rem', letterSpacing: 0.2 }}>
      {label}
    </span>
    <h4 className="mb-0 fw-bold" style={{ fontSize: '1.55rem', color }}>
      {value}
    </h4>
  </div>
);

const PeriodTile = ({ label, value, previousValue, delta, t }) => (
  <div
    className="d-flex flex-column align-items-start"
    style={{
      minWidth: 160,
      padding: '12px 14px',
      border: '1px solid #eef1f5',
      borderRadius: 12,
      background: '#fff',
      flex: '1 1 0',
    }}
  >
    <span className="text-muted" style={{ fontSize: '0.78rem', letterSpacing: 0.2 }}>
      {label}
    </span>
    <h4 className="mb-1 fw-bold" style={{ fontSize: '1.55rem' }}>{formatHours(value)}</h4>
    <div className="d-flex align-items-center gap-2">
      <span className="text-muted" style={{ fontSize: '0.72rem' }}>
        {previousValue != null
          ? t('reporting.mtbf.from', 'from {{value}}', { value: formatHours(previousValue) })
          : t('reporting.mtbf.no_previous', 'no prior data')}
      </span>
      <DeltaIndicator delta={delta} />
    </div>
  </div>
);

const ChartTooltip = ({ active, payload }) => {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '10px 12px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        fontSize: '0.82rem',
      }}
    >
      <div className="fw-bold mb-1">{point.label}</div>
      <div>
        {t('reporting.mtbf.title', 'Mean Time Between Failures')}:{' '}
        <span className="fw-bold">{formatHours(point.mtbfHours)}</span>
      </div>
      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
        {t('reporting.mtbf.failures', 'Failures')}: {point.failures}
      </div>
    </div>
  );
};

/**
 * Self-fetching by default. When {@code data} is passed externally the
 * widget skips its API call and renders the supplied payload — used by
 * the Global Dashboard to embed the same JSX cross-org.
 */
const MtbfWidget = ({
  organizationId,
  dateRange,
  criticality,
  locationId,
  vendorId,
  assetId,
  assetTypeId,
  contractNumber,
  workOrderCategoryId,
  data: externalData,
  loading: externalLoading,
  error: externalError,
}) => {
  const { t } = useTranslation();
  const externalMode = externalData !== undefined;
  const [internalData, setInternalData] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const data = externalMode ? externalData : internalData;
  const loading = externalMode ? !!externalLoading : internalLoading;
  const error = externalMode ? externalError : internalError;

  const fetchReport = useCallback(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    setInternalLoading(true);
    setInternalError(null);
    const params = {
      organizationId,
      startDate: formatDateTimeForAPI(dateRange.startDate),
      endDate: formatDateTimeForAPI(dateRange.endDate),
    };
    if (criticality) params.criticality = criticality;
    if (locationId) params.locationId = locationId;
    if (vendorId) params.vendorId = vendorId;
    if (assetId) params.assetId = assetId;
    if (assetTypeId) params.assetTypeId = assetTypeId;
    if (contractNumber) params.contractNumber = contractNumber;
    if (workOrderCategoryId) params.workOrderCategoryId = workOrderCategoryId;

    api.get('reporting/assets/mtbf', { params })
      .then(({ data: res }) => {
        setInternalData(res?.response || null);
        setInternalLoading(false);
      })
      .catch((e) => {
        setInternalError(e?.message || t('shared.error_loading', 'Error loading data'));
        setInternalLoading(false);
      });
  }, [externalMode, organizationId, dateRange?.startDate, dateRange?.endDate, criticality, locationId, vendorId,
      assetId, assetTypeId, contractNumber, workOrderCategoryId, t]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const chartPoints = data?.chart?.points || [];
  const isEmpty = !data || !chartPoints.length;
  const hasActiveFilters = criticality || locationId || vendorId || assetId ||
    assetTypeId || contractNumber || workOrderCategoryId;

  const summary = data?.summary;
  const counters = data?.counters;

  const chartData = useMemo(() => chartPoints.map(p => ({
    label: p.label,
    mtbfHours: p.mtbfHours,
    failures: p.failures,
  })), [chartPoints]);

  return (
    <ReportWidget
      title={
        <span className="d-flex align-items-center gap-2">
          {t('reporting.mtbf.title', 'Mean Time Between Failures')}
          <Info size={15} className="text-muted" id="mtbfTooltip" style={{ cursor: 'help' }} />
          <UncontrolledTooltip placement="top" target="mtbfTooltip">
            {t('reporting.mtbf.tooltip',
              'MTBF measures asset reliability: operating time minus unplanned downtime, divided by the number of failures. Custom downtime reasons and Do-Not-Track periods are excluded.')}
          </UncontrolledTooltip>
        </span>
      }
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={hasActiveFilters
        ? t('reporting.mtbf.empty_filtered', 'No MTBF data found for the selected filters.')
        : t('reporting.mtbf.empty', 'No MTBF data available for the selected period.')}
      onRefresh={externalMode ? undefined : fetchReport}
    >
      {/* Counters + summary tiles */}
      {(counters || summary) && (
        <div className="d-flex flex-wrap gap-3 mb-4">
          <CounterTile
            value={counters?.totalFailures ?? 0}
            label={t('reporting.mtbf.total_failures', 'Total Failures')}
            color={COLOR_DOWN}
          />
          <CounterTile
            value={counters?.failuresOnCriticalAssets ?? 0}
            label={t('reporting.mtbf.failures_on_critical', 'Failures on Critical Assets')}
            color={COLOR_DOWN}
          />
          <PeriodTile
            label={t('reporting.mtbf.this_month', 'This Month')}
            value={summary?.thisMonth?.mtbfHours}
            previousValue={summary?.thisMonth?.previousMtbfHours}
            delta={summary?.thisMonth?.deltaPercent}
            t={t}
          />
          <PeriodTile
            label={t('reporting.mtbf.last_month', 'Last Month')}
            value={summary?.lastMonth?.mtbfHours}
            previousValue={summary?.lastMonth?.previousMtbfHours}
            delta={summary?.lastMonth?.deltaPercent}
            t={t}
          />
          <PeriodTile
            label={t('reporting.mtbf.year_to_date', 'Year-to-date')}
            value={summary?.yearToDate?.mtbfHours}
            previousValue={summary?.yearToDate?.previousMtbfHours}
            delta={summary?.yearToDate?.deltaPercent}
            t={t}
          />
        </div>
      )}

      {/* MTBF line chart */}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} label={{ value: t('reporting.mtbf.hours_axis', 'Hours'), angle: -90, position: 'insideLeft', style: { fontSize: 12 } }} />
          <Tooltip content={<ChartTooltip />} />
          <Line
            type="monotone"
            dataKey="mtbfHours"
            stroke={COLOR_PRIMARY}
            strokeWidth={2}
            dot={{ r: 3, fill: COLOR_PRIMARY }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            name={t('reporting.mtbf.title', 'Mean Time Between Failures')}
          />
        </LineChart>
      </ResponsiveContainer>
    </ReportWidget>
  );
};

export default MtbfWidget;
